import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { safeContinuePath } from '../utils/safeContinuePath';
import { OnboardingBackdrop } from './onboarding/OnboardingLayout';

/**
 * What the no-token screen knows about the user's verification link, from
 * GET /api/auth/verify-email/status. `unknown` covers a missing session or a
 * failed lookup — the screen then claims nothing and offers to send.
 */
type LinkStatus =
  | { kind: 'loading' }
  | { kind: 'verified' }
  | { kind: 'pending' }
  | { kind: 'none' }
  | { kind: 'unknown' };

/**
 * VerifyEmail page - handles email verification flow.
 *
 * Two modes:
 * 1. Token present: Validates and completes verification
 * 2. No token: Shows the real state of the user's verification link — "we
 *    sent you a link" only when the server confirms one is outstanding —
 *    with a send/resend option
 */
const AUTHORIZE_PATH = '/api/oidc/authorize';

/** Map a pending app authorize continuation to the app's fresh entry. */
export function appEntryForReturnTo(returnTo: string): string {
  if (returnTo === AUTHORIZE_PATH || returnTo.startsWith(`${AUTHORIZE_PATH}?`)) {
    return `/api/oidc/app-entry?continue=${encodeURIComponent(returnTo)}`;
  }
  return returnTo;
}

/**
 * Verifying an address is part of the sign-up journey, so it wears the same
 * frame as the onboarding steps that follow it — the LevelUp backdrop and
 * logo, not a bare card on grey. Without this the journey changed its look
 * twice before the user reached step one.
 */
function VerifyShell({ children }: { children: ReactNode }) {
  return (
    <OnboardingBackdrop>
      <div className="flex flex-1 items-center justify-center p-4">{children}</div>
    </OnboardingBackdrop>
  );
}

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [linkStatus, setLinkStatus] = useState<LinkStatus>({ kind: 'loading' });

  const token = searchParams.get('token');
  // The journey to resume after verifying (e.g. /start/business). It arrives
  // in emailed links, so an off-origin value is dropped.
  const rawReturnTo = safeContinuePath(searchParams.get('return_to')) ?? '';
  // An app sign-in that was waiting on verification (`/api/oidc/authorize?…`)
  // must NOT be replayed here: the emailed link usually opens in a new tab,
  // and the app's sign-in state lives in the tab that started it, so the app
  // would show "Sign-in didn't finish". Send the user to the app's own entry
  // instead (`/api/oidc/app-entry` validates the client + redirect_uri and
  // redirects to the app), which starts a fresh sign-in in THIS tab and
  // completes silently on the Central session.
  const returnTo = appEntryForReturnTo(rawReturnTo);
  const signInUrl = returnTo ? `/auth/login?continue=${encodeURIComponent(returnTo)}` : '/auth/login';
  // Where a user who is ALREADY signed in to Central goes: straight back into
  // the journey (or /home, which resumes an unfinished workspace at its step)
  // — never the password page. Sending a signed-in user to sign in again is
  // how verifying mid-onboarding used to end on the chooser and reset it.
  const signedInContinueUrl = returnTo || '/home';
  const [hasSession, setHasSession] = useState(false);
  // A reused or expired link: the error code, and whether this browser still
  // holds a Central session (then the user continues instead of signing in).
  const [errorCode, setErrorCode] = useState('');
  const [signedInAfterError, setSignedInAfterError] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
      loadLinkStatus();
    }
  }, [token]);

  const loadLinkStatus = async () => {
    try {
      const response = await fetch('/api/auth/verify-email/status', { credentials: 'include' });
      if (!response.ok) {
        setLinkStatus({ kind: 'unknown' });
        return;
      }
      const data = await response.json();
      if (data.verified) {
        setLinkStatus({ kind: 'verified' });
      } else if (data.pending) {
        setLinkStatus({ kind: 'pending' });
      } else {
        setLinkStatus({ kind: 'none' });
      }
    } catch {
      setLinkStatus({ kind: 'unknown' });
    }
  };

  const verifyToken = async () => {
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Verification failed');
        const code = typeof data.code === 'string' ? data.code : '';
        setErrorCode(code);
        // A REUSED OR EXPIRED LINK IS NOT A SIGN-OUT. Opening the same email
        // link twice, or an old one, in the browser the user signed up in used
        // to end on "Back to sign in" although their session was still valid.
        // The session-only status call says whether one is present; if so the
        // user continues into their workspace (and can get a fresh link).
        if (code === 'TOKEN_USED' || code === 'TOKEN_EXPIRED') {
          try {
            const statusRes = await fetch('/api/auth/verify-email/status', { credentials: 'include' });
            setSignedInAfterError(statusRes.ok);
          } catch {
            // No session information — keep the sign-in link.
          }
        }
        return;
      }

      setIsVerified(true);
      setEmail(data.email || '');
      // Is this browser signed in as the user who just verified? The status
      // endpoint answers only for a Central session, and reports `verified`
      // for the session's own user — so a different (unverified) account
      // signed in here is not mistaken for this one. Any doubt → sign in.
      try {
        const statusRes = await fetch('/api/auth/verify-email/status', { credentials: 'include' });
        if (statusRes.ok) {
          const status = await statusRes.json().catch(() => ({}));
          const signedIn = status?.verified === true;
          setHasSession(signedIn);
          // Verified and signed in here: take the user straight on to the
          // app / journey instead of leaving them on a confirmation page.
          if (signedIn) {
            window.location.assign(returnTo || '/home');
          }
        }
      } catch {
        // No session information — keep the sign-in continuation.
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const response = await fetch('/api/auth/verify-email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(returnTo ? { return_to: returnTo } : {}),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setError('Please wait before requesting another email.');
        } else {
          setError(data.error || 'Failed to resend email');
        }
        return;
      }

      if (data.message === 'Email already verified') {
        setLinkStatus({ kind: 'verified' });
        return;
      }
      setResendSuccess(true);
      setLinkStatus({ kind: 'pending' });
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <VerifyShell>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 w-full max-w-md text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Verifying your email...</p>
        </div>
      </VerifyShell>
    );
  }

  // Verification successful
  if (isVerified) {
    return (
      <VerifyShell>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Email verified!
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {email ? `Your email ${email} has been verified.` : 'Your email has been verified.'}
          </p>
          {hasSession ? (
            // A full navigation: the journey may be an /api/oidc/* URL.
            <a
              href={signedInContinueUrl}
              className="inline-block bg-emerald-600 text-white py-2 px-6 rounded-md font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 transition-colors"
            >
              Continue
            </a>
          ) : (
            <Link
              to={signInUrl}
              className="inline-block bg-emerald-600 text-white py-2 px-6 rounded-md font-medium hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 transition-colors"
            >
              Continue to sign in
            </Link>
          )}
        </div>
      </VerifyShell>
    );
  }

  // Error state (from token verification)
  if (error && token) {
    return (
      <VerifyShell>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {errorCode === 'TOKEN_USED' ? 'This link was already used' : 'Verification failed'}
          </h1>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          {signedInAfterError && errorCode === 'TOKEN_EXPIRED' && (
            <div className="mb-4">
              {/* A successful resend moves to the "check your email" view. */}
              <button
                onClick={handleResend}
                disabled={isResending}
                data-testid="verify-email-resend-expired"
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {isResending ? 'Sending...' : 'Send me a new link'}
              </button>
            </div>
          )}
          {signedInAfterError ? (
            <a
              href={signedInContinueUrl}
              data-testid="verify-email-continue"
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Continue
            </a>
          ) : (
            <Link
              to={signInUrl}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Back to sign in
            </Link>
          )}
        </div>
      </VerifyShell>
    );
  }

  // No token - show "check your email" page
  return (
    <VerifyShell>
      <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 w-full max-w-md text-center">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-6 h-6 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        {linkStatus.kind === 'loading' ? (
          <p className="text-sm text-gray-500 mb-6">Checking your verification status...</p>
        ) : linkStatus.kind === 'verified' ? (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Your email is verified
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              {/* `verified` here came from the session's own status, so this
                  browser is signed in: continue the journey directly. */}
              <a href={signedInContinueUrl} className="text-blue-600 hover:text-blue-800">
                Continue
              </a>
            </p>
          </>
        ) : linkStatus.kind === 'pending' ? (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Check your email
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-testid="verify-email-sent">
              We sent you a verification link. Please check your inbox and spam folder.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              Verify your email
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-testid="verify-email-not-sent">
              Verify your email address to continue. Send yourself a verification link below.
            </p>
          </>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {resendSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">Verification email sent!</p>
          </div>
        )}

        {linkStatus.kind !== 'loading' && linkStatus.kind !== 'verified' && (
          <button
            onClick={handleResend}
            disabled={isResending}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {isResending
              ? 'Sending...'
              : linkStatus.kind === 'pending'
                ? "Didn't receive it? Resend email"
                : 'Send verification email'}
          </button>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <Link
            to={signInUrl}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </VerifyShell>
  );
}
